import {
    addFrontendPropertiesToSingle,
    addFrontendPropertiesToTentatives,
    addIndexesAsOrderToTentatives,
} from './addFrontendProperties.js';
import { createTentativeSection, createTentativeService, detachAllServicesRelatedTo } from './createTentatives.js';
import { tentativeServicesFromTentatives } from './tentativeHelpers.js';

export const tentatives = {
    namespaced: true,
    state: {
        modifiedTentatives: {},
        modifiedTentativeServices: [],
        modifiedTentativeConfig: [],
        prices: null,
        deletedTentatives: {
            services: [],
            sections: [],
            rates: [],
            bookings: [],
            packages: [],
        },
    },
    actions: {
        deleteSection(context, payload) {
            payload.tentativeSection.tentativeServices.forEach((tentativeService) => {
                context.commit('deleteService', {
                    tentativeSection: payload.tentativeSection,
                    tentativeService: tentativeService,
                });
            });
            context.commit('deleteSection', payload);
        },
        deleteService(context, payload) {
            context.commit('deleteService', payload);
            if (payload.tentativeService.rate) {
                context.commit('deleteRate', payload.tentativeService.rate);
            }
            if (payload.tentativeService.booking) {
                context.commit('deleteBooking', payload.tentativeService.booking);
            }
        },
    },
    mutations: {
        // Set
        setTentativeConfig(state, tentativeConfig) {
            state.modifiedTentativeConfig = tentativeConfig;
        },
        setTentatives(state, tentatives) {
            state.modifiedTentatives = addFrontendPropertiesToTentatives(tentatives, state.modifiedTentativeConfig);
        },
        setPrices(state, prices) {
            state.prices = prices;
        },
        resetDeletedTentatives(state) {
            state.deletedTentatives = {
                services: [],
                sections: [],
                rates: [],
                bookings: [],
                packages: [],
            };
        },

        // Create
        createSection(state, payload) {
            createTentativeSection(state, payload.lineType, payload.event, payload.tentativePackage);
        },
        createService(state, payload) {
            createTentativeService(state, payload.lineType, payload.tentativeSection, payload.event);
        },

        // Delete
        deleteSection(state, payload) {
            state.deletedTentatives.sections.push(payload.tentativeSection);
            if (payload.tentativePackage) {
                payload.tentativePackage.tentativeSections.splice(
                    payload.tentativePackage.tentativeSections.indexOf(payload.tentativeSection),
                    1
                );
            } else {
                state.modifiedTentatives[payload.lineType].splice(
                    state.modifiedTentatives[payload.lineType].indexOf(payload.tentativeSection),
                    1
                );
            }
        },
        deleteService(state, payload) {
            detachAllServicesRelatedTo(state, payload);
            state.deletedTentatives.services.push(payload.tentativeService);
            const index = payload.tentativeSection.tentativeServices.indexOf(payload.tentativeService);
            if (index !== -1) {
                payload.tentativeSection.tentativeServices.splice(index, 1);
            }
        },
        deleteRate(state, tentativeService) {
            state.deletedTentatives.rates.push(tentativeService.rate);
        },
        deleteBooking(state, tentativeService) {
            state.deletedTentatives.bookings.push(tentativeService.booking);
        },

        swapService(state, payload) {
            // Remove the service from the section
            const index = payload.tentativeSection.tentativeServices.indexOf(payload.tentativeService);
            state.deletedTentatives.services.push(payload.tentativeService);
            payload.tentativeSection.tentativeServices.splice(index, 1);

            // Add the new service to the section
            let swapWith = {
                ...payload.swapWith,
                // Ensure settings object is unique per service instance
                settings: payload.swapWith.settings ? { ...payload.swapWith.settings } : undefined,
            };

            addFrontendPropertiesToSingle(swapWith);
            swapWith.external_id = payload.tentativeService.external_id;
            swapWith.package_grouping_id = payload.tentativeService.package_grouping_id;
            swapWith.quantity = payload.tentativeService.quantity;
            swapWith.line_type = payload.tentativeService.line_type;
            swapWith.price_vat = payload.tentativeService.price_vat;
            swapWith.price = payload.tentativeService.price;
            swapWith.vat_percentage = payload.tentativeService.vat_percentage;
            payload.tentativeSection.tentativeServices.splice(index, 0, swapWith);

            // Re-attach services related to the detached service
            payload.swapWith = swapWith;
        },

        attachServiceToService(state, payload) {
            payload.tentativeService.parentTentativeService = payload.attachTo;
        },

        updateService(state, payload) {
            const { tentativeService, updates } = payload;
            if (!tentativeService || !updates) return;
            Object.keys(updates).forEach((key) => {
                tentativeService[key] = updates[key];
            });
        },

        updateSection(state, payload) {
            const { tentativeSection, updates } = payload;
            if (!tentativeSection || !updates) return;
            Object.keys(updates).forEach((key) => {
                tentativeSection[key] = updates[key];
            });
        },

        updatePackage(state, payload) {
            const { tentativePackage, updates } = payload;
            if (!tentativePackage || !updates) return;
            Object.keys(updates).forEach((key) => {
                tentativePackage[key] = updates[key];
            });
        },

        spliceServiceInSection(state, payload) {
            const { tentativeSection, serviceIndex, deleteCount, insertedService } = payload;
            if (insertedService != null) {
                tentativeSection.tentativeServices.splice(serviceIndex, deleteCount, insertedService);
            } else {
                tentativeSection.tentativeServices.splice(serviceIndex, deleteCount);
            }
        },

        // Other
        setOrderOnTentatives(state) {
            state.modifiedTentatives = addIndexesAsOrderToTentatives(
                state.modifiedTentatives,
                state.modifiedTentativeConfig
            );
        },
    },
    getters: {
        lineTypes(state) {
            if (state.modifiedTentativeConfig.type === 'package') {
                return ['package', 'selected', 'available'];
            } else {
                // Hide package lines if empty
                if (
                    state.modifiedTentatives.package === undefined ||
                    (state.modifiedTentatives.package && state.modifiedTentatives.package.length === 0)
                ) {
                    return ['selected', 'available'];
                } else {
                    return ['package', 'selected', 'available'];
                }
            }
        },
        tentatives(state) {
            return state.modifiedTentatives;
        },
        tentativeConfig(state) {
            return state.modifiedTentativeConfig;
        },
        deletedTentatives(state) {
            return state.deletedTentatives;
        },
        tentativeServices(state) {
            return tentativeServicesFromTentatives(state.modifiedTentatives);
        },
        tentativePrices(state) {
            return state.prices;
        },
    },
};
